/**
 * The app's one destructive/consequential confirm: a title, a sentence, and two answers.
 *
 * Promoted out of `pages/plan-builder` on its second consumer. The first is the structural-save
 * consent — "this save moves your own bookings" — and the second is removing a set-grid column,
 * which discards what the trainer typed into it. They are the same object: a modal that states a
 * consequence and asks the trainer to take it or leave it, and a second copy of the surface is the
 * near-duplicate-class failure the house rule exists to prevent.
 *
 * It words nothing. Every string arrives on `prompt`, already resolved by the model that knows what
 * is about to happen — which is what lets a test read the sentence without a renderer.
 *
 * ── `AlertDialog`, not `Dialog` ──────────────────────────────────────────────────────────────
 * It cannot be dismissed by a click into nothing. The trainer took an action and this is the answer
 * to that action, so it has to be answered. Escape maps to the CANCEL, which is always the
 * non-destructive outcome — nothing is committed, and for the structural save that means the plan
 * stays exactly as edited and unsaved.
 *
 * ── Cancel is first in the DOM, and therefore first to focus ─────────────────────────────────
 * A trainer pressing Enter on reflex must not commit the consequence the dialog just described.
 *
 * ── A THIRD answer, when the situation genuinely has one ─────────────────────────────────────
 * Two answers is the shape of "take it or leave it", and the leave guard is not that: a trainer with
 * unsaved work has three real moves — stay, leave and lose it, or save and then leave. Folding the
 * third into a second dialog would ask the same question twice, and dropping it makes the guard
 * offer only the two outcomes the trainer did not want.
 *
 * It is OPTIONAL and every other caller omits it, so nothing pays for it. It is not a seam for a
 * hypothetical variation either: the alternative is a copy of this surface for one screen, which is
 * the near-duplicate-class failure the house rule exists to prevent.
 */

import { useId, type ReactNode } from 'react';

import { AlertDialog } from '@base-ui/react/alert-dialog';

import { wrapTab } from '~/shared/lib/wrapTab';

import surface from './dialogSurface.module.css';
import styles from './ConfirmDialog.module.css';

export interface ConfirmPrompt {
  readonly title: string;
  /**
   * WHAT the act is about — a client's name, a phase's name — drawn as its own line above the
   * description.
   *
   * It exists because Polish declines and English does not. "Zakończyć współpracę z {{name}}?"
   * printed "z Kuba Zieliński" where the language wants the instrumental "z Kubą Zielińskim", and
   * "dla {{name}}?" wants the genitive — two different cases, from a name the app cannot inflect
   * and must not try to. Lifting the name out of the sentence leaves it in the nominative, which is
   * the one form that is always correct, and the question stops needing a grammatical hole in it.
   *
   * It stays inside the description region rather than beside the title, so the dialog is announced
   * as question, subject, consequence — the order a listener can follow.
   */
  readonly subject?: string;
  readonly description: string;
  /**
   * The description is several LINES (`\n`-separated) and its breaks are part of what it says — a
   * list of plans, one per line. Optional, and off for every other caller, whose single sentence
   * reads exactly as before.
   */
  readonly multiline?: boolean;
  /**
   * What the confirm will act on, drawn under the description when one sentence cannot carry it:
   * „Dodaj wszystkie szybko" lists every exercise it adds, each with its proposal. Plain content
   * only — the answers stay this dialog's own.
   */
  readonly details?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /**
   * A third answer, drawn between Cancel and Confirm as a second ghost. Omitted by every caller but
   * the leave guard. Present exactly when `onAlternate` is — the two are one answer split across
   * the prompt and the handlers, the same way the other two labels already are.
   */
  readonly alternateLabel?: string;
  /**
   * Why the third answer cannot act right now, or absent when it can.
   *
   * The leave guard is the case: „Zapisz i wyjdź" cannot save a form the save itself refuses, and
   * the sentence saying so is rendered beside the save button on the screen this card is standing
   * in front of. Without it the button was live-looking and did nothing at all when pressed.
   *
   * It carries the same `aria-disabled` treatment `busy` does, and the reason is RENDERED and
   * pointed at by `aria-describedby` — a `title` reaches neither a touch user nor a keyboard user
   * who never hovers.
   */
  readonly alternateRefusal?: string;
  /**
   * The confirm DESTROYS something and there is no answer here that preserves it.
   *
   * It takes the ember OFF the confirm rather than moving it onto Cancel: the accent marks a real
   * action and "Anuluj" is not one, so a destructive prompt has no ember at all. The confirm is
   * filled in `--danger` instead — see the stylesheet for the measurement that reversed the earlier
   * arrangement — and Cancel is the plain text button every dialog's Cancel now is.
   *
   * **Focus is unaffected and stays on Cancel.** Cancel is first in the DOM, so Enter on reflex
   * still commits nothing. Emphasis says what a button does; focus says what a reflex does.
   *
   * Never on a prompt whose confirm is constructive: the leave guard's THREE-button variant confirms
   * with `Zapisz i wyjdź`, and the ember belongs there.
   */
  readonly destructiveConfirm?: boolean;
  /** The prompt names a person's address: kept out of session replay (Clarity on staging). */
  readonly masked?: boolean;
  /**
   * The THIRD answer destroys something too — it destroys LESS.
   *
   * The removal's scope question is the case that needs it: both answers take a training out of the
   * plan and they differ only in how far they reach, so `Tylko ten tydzień` beside `Ten tydzień i
   * wszystkie kolejne` is the narrower destruction, not the safe way out. Drawn as the ordinary
   * ghost it read as one — and the same words turn a filled `--danger` red when that scope is the
   * only legal one, which is the same act wearing two opposite treatments one dialog apart.
   *
   * It is the danger OUTLINE: the ink and the edge of `.danger` with no fill. That keeps the
   * emphasis order the dialog needs — Cancel neutral, the alternate marked, the confirm loudest —
   * while saying that the middle answer is not a retreat. Never set it on a prompt whose alternate
   * preserves what the confirm destroys: the leave guard's `Zapisz i wyjdź` is the opposite of this.
   */
  readonly destructiveAlternate?: boolean;
  /**
   * The act this dialog dispatched is still running.
   *
   * EVERY answer stops responding, Cancel included. An earlier revision left Cancel live on the
   * reasoning that staying costs nothing — but the callers that raise a busy dialog have no request
   * cancellation to offer, so pressing it did precisely nothing while it went on drawing itself as
   * the one live control on a card whose other two had visibly gone quiet. A control that cannot act
   * must not look as though it can; the way to call the act off is not to start it.
   *
   * `aria-disabled` rather than the attribute, so every busy control keeps its place in the tab
   * order and its label stays reachable (view ADR §12).
   */
  readonly busy?: boolean;
}

interface ConfirmDialogProps {
  /** `null` closes it. Non-null exactly while an act is waiting on an answer. */
  readonly prompt: ConfirmPrompt | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** The third answer's act. Omit it — and `alternateLabel` — for a two-answer dialog. */
  readonly onAlternate?: (() => void) | undefined;
  /**
   * Where focus goes when this closes, for the answers whose own opener will not be there to take
   * it back.
   *
   * Omitted, Base UI returns focus to whatever was focused when the dialog opened — for a prompt
   * raised from a row's ⋯ that is the ⋯ itself, which is right for every answer that leaves the row
   * alone. It is NOT right for the answer that deletes the row: the return target is unmounted by
   * the time the dialog closes, and focus lands on `<body>` — no announcement, no tab position, and
   * the next keypress goes nowhere.
   *
   * So a caller whose confirm removes its own opener hands this over for that answer and omits it
   * for the others. See `pages/client-roster` for the wiring, and note that the target has to be
   * focusable — every screen's `h1` carries `tabIndex={-1}` for exactly this.
   */
  readonly finalFocus?: React.RefObject<HTMLElement | null> | undefined;
}

export function ConfirmDialog({
  prompt,
  onConfirm,
  onCancel,
  onAlternate,
  finalFocus,
}: ConfirmDialogProps) {
  const refusalId = useId();
  const busy = prompt?.busy === true;
  const alternateRefusal = prompt?.alternateRefusal;
  const alternateOff = busy || alternateRefusal !== undefined;
  // Cancel is always the neutral outline. A destructive confirm swaps the ember for a `--danger`
  // fill rather than handing the ember to the answer that does nothing.
  const destructive = prompt?.destructiveConfirm === true;
  const confirmClass = destructive ? surface.danger : surface.primary;
  // The middle answer, when it destroys as well — the danger edge without the fill, so the two
  // destructive answers read as the same KIND of act at different sizes.
  const alternateClass =
    prompt?.destructiveAlternate === true ? surface.dangerGhost : surface.ghost;
  return (
    <AlertDialog.Root
      open={prompt !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={surface.backdrop} />
        {/* `finalFocus` is the CALLER's, and only for the answers that need it. Base UI's default
            returns focus to the control that was focused when this opened — for a dialog raised
            from a menu item, the menu's own trigger, which is exactly where a keyboard user needs
            to land after cancelling. An earlier revision concluded from that the prop was never
            needed; it is, on every prompt whose confirm deletes the control it was raised from. */}
        <AlertDialog.Popup
          className={surface.popup}
          data-clarity-mask={prompt?.masked === true ? 'True' : undefined}
          finalFocus={finalFocus}
          onKeyDown={(event) => {
            wrapTab(event, event.currentTarget);
          }}
        >
          <AlertDialog.Title className={surface.title}>{prompt?.title}</AlertDialog.Title>
          <AlertDialog.Description
            className={
              prompt?.multiline === true
                ? `${String(surface.description)} ${String(styles.lines)}`
                : surface.description
            }
          >
            {prompt?.subject !== undefined && (
              <span className={styles.subject}>{prompt.subject}</span>
            )}
            {prompt?.description}
          </AlertDialog.Description>
          {prompt?.details}
          {alternateRefusal === undefined ? null : (
            <p id={refusalId} className={styles.refusal}>
              {alternateRefusal}
            </p>
          )}
          <div className={surface.actions}>
            {/* Cancel is not one of the answers, and since 15 August 2026 it is not drawn as one —
                see the stylesheet. It stays FIRST in the DOM, which is the safety property: focus
                lands here, so Enter on reflex commits nothing. */}
            <button
              type="button"
              className={surface.cancel}
              aria-disabled={busy ? 'true' : undefined}
              onClick={busy ? undefined : onCancel}
            >
              {prompt?.cancelLabel}
            </button>
            {onAlternate !== undefined && prompt?.alternateLabel !== undefined && (
              <button
                type="button"
                className={alternateClass}
                aria-disabled={alternateOff ? 'true' : undefined}
                aria-describedby={alternateRefusal === undefined ? undefined : refusalId}
                onClick={alternateOff ? undefined : onAlternate}
              >
                {prompt.alternateLabel}
              </button>
            )}
            <button
              type="button"
              className={confirmClass}
              aria-disabled={busy ? 'true' : undefined}
              onClick={busy ? undefined : onConfirm}
            >
              {prompt?.confirmLabel}
            </button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
